import React from 'react';

import compact from 'lodash/compact';

import { useDataAttributes } from '@lumapps/data-attributes';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { InsertVideoDialog } from '@lumapps/lumx-preview/components/InsertUrlPreviewDialog';
import { mdiLinkVariant, mdiVideoOutline } from '@lumapps/lumx/icons';
import { getEmbedUrl } from '@lumapps/medias/utils';
import { useGooglePickerButton } from '@lumapps/play-communities/hooks/useGooglePickerButton';
import { useVideoUploadButton } from '@lumapps/play-communities/hooks/useVideoUploadButton';
import { CommunityVideo } from '@lumapps/play-communities/types';
import { getFullVideoLink } from '@lumapps/play-video/utils/getFullVideoLink';
import { Video } from '@lumapps/play/api/types';
import { useRouter } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { LinkAttachment, LinkResourceType } from '@lumapps/widget-base/types';
import { usePickPlayVideoButton } from '@lumapps/wrex-play-video';
import { PlayVideoEditor } from '@lumapps/wrex-play-video/types';
import { ReactEditor, useSlateStatic } from '@lumapps/wrex/slate';
import { ToolbarItem } from '@lumapps/wrex/types';

import { WREX_EMBEDDED_VIDEO } from '../../../keys';
import { EmbeddedVideoEditor } from '../../../types';

export type UseEmbeddedVideoDropdownProps = {
    onInsertLink?(video: LinkAttachment): void;
    otherProps?: any;
};

export const useEmbeddedVideoDropdown = ({ onInsertLink, otherProps }: UseEmbeddedVideoDropdownProps) => {
    const { get } = useDataAttributes('embedded-video-dropdown');
    const editorStatic = useSlateStatic() as ReactEditor & PlayVideoEditor & EmbeddedVideoEditor;
    const { getUrl } = useRouter();

    const getFormattedPlayVideoItem = React.useCallback(
        (video: Video | CommunityVideo): LinkAttachment => ({
            resourceId: video.id,
            resourceType: LinkResourceType.PLAY_VIDEO,
            title: video.title.value ?? '',
            description: '',
            url: getFullVideoLink({ videoId: video.id }, getUrl),
        }),
        [getUrl],
    );

    const onInsertVideo = (video: LinkAttachment) => {
        editorStatic.insertEmbeddedVideo({
            url: getEmbedUrl(video.url),
            title: video.title,
            thumbnailUrl: video?.images && video.images[0],
            description: video.description,
        });
    };

    const onInsertPlayVideo = (video: Video | CommunityVideo) => {
        if (video) {
            editorStatic.insertPlayVideo({
                id: video.id,
                title: video.title.value,
                url: getFullVideoLink({ videoId: video.id }, getUrl),
            });
        }
    };

    const { insertPlayVideoDialog, PickPlayVideoButtonProps } = usePickPlayVideoButton({
        onInsert: (video) => {
            if (onInsertLink) {
                onInsertLink(getFormattedPlayVideoItem(video));
            } else {
                onInsertPlayVideo(video);
            }
        },
        otherProps: {
            ...get({ element: 'button', action: 'pick-video-from-gallery' }),
        },
    });

    const { googlePickerButtonProps, GoogleUploadDialog } = useGooglePickerButton({
        onInsert: (video) => {
            if (onInsertLink) {
                onInsertLink(getFormattedPlayVideoItem(video));
            } else {
                onInsertPlayVideo(video);
            }
        },
    });

    const {
        uploadButtonProps,
        UploadInput: videoUploadInput,
        UploadDialog,
    } = useVideoUploadButton({
        onInsert: (video) => {
            if (onInsertLink) {
                onInsertLink(getFormattedPlayVideoItem(video));
            } else {
                onInsertPlayVideo(video);
            }
        },
    });

    const { translateKey } = useTranslate();
    const [isVideoLinkDialogOpen, , closeVideoLinkDialog, openVideoLinkDialog] = useBooleanState(false);

    const insertLinkVideoDialog = (
        <InsertVideoDialog
            isOpen={isVideoLinkDialogOpen}
            onInsert={(video) => {
                (onInsertLink || onInsertVideo)(video);
                closeVideoLinkDialog();
            }}
            onClose={closeVideoLinkDialog}
        />
    );

    const fromUrlButton: ToolbarItem = {
        itemKey: 'video-from-url',
        type: 'option',
        onClick: openVideoLinkDialog,
        icon: mdiLinkVariant,
        label: translateKey(WREX_EMBEDDED_VIDEO.ENTER_URL),
        otherProps: { ...get({ element: 'option', action: 'open-insert-url-dialog' }) },
    };

    const embeddedVideoDropdownOptions: ToolbarItem[] = compact([
        uploadButtonProps && {
            itemKey: 'video-from-device',
            type: 'option',
            onClick: () => uploadButtonProps.onSelected?.(),
            icon: uploadButtonProps.icon,
            label: translateKey(uploadButtonProps.label),
            otherProps: { ...get({ element: 'option', action: 'upload-video-from-device' }) },
        },
        PickPlayVideoButtonProps,
        googlePickerButtonProps && {
            ...googlePickerButtonProps,
            itemKey: 'google-picker',
            type: 'option',
            onClick: () => googlePickerButtonProps.onSelected(),
            otherProps: { ...get({ element: 'option', action: 'upload-video-from-google-drive' }) },
        },
        fromUrlButton,
    ]);

    const buttonProps: Omit<ToolbarItem, 'type'> = {
        itemKey: 'embedded-video',
        icon: mdiVideoOutline,
        label: translateKey(GLOBAL.VIDEO),
        tooltipLabel: translateKey(WREX_EMBEDDED_VIDEO.INSERTION_BUTTON_TOOLTIP),
        variant: 'icon',
        otherProps,
    };

    const dropdown: ToolbarItem = {
        ...buttonProps,
        type: 'submenu',
        childrenOptions: embeddedVideoDropdownOptions,
    };

    const embeddedVideoButton: ToolbarItem =
        embeddedVideoDropdownOptions.length > 1
            ? dropdown
            : {
                  ...buttonProps,
                  type: 'option',
                  onClick: fromUrlButton.onClick,
              };

    return {
        GoogleUploadDialog,
        embeddedVideoButton,
        videoUploadInput,
        insertPlayVideoDialog,
        videoUploadDialog: UploadDialog,
        insertLinkVideoDialog,
    };
};
